import { useState } from "react";
import { toast } from "sonner";
import { MessageCircle, Printer } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { useBranch, withBranch } from "@/lib/branch";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { fmtDateTime, rupiah, TX_LABELS } from "@/lib/format";

const receiptLines = (tx) => {
  const lines = [["Jenis", TX_LABELS[tx.type]], ["Waktu", fmtDateTime(tx.created_at)], ["No. Ref", tx.id.slice(0, 8).toUpperCase()],
    ["Anggota", tx.member_name], ["NIK", tx.nik]];
  if (tx.resort_name) lines.push(["Resort", tx.resort_name]);
  if (tx.loan_no) lines.push(["No. Pinjaman", tx.loan_no]);
  lines.push(["Nominal", rupiah(tx.amount)]);
  if (tx.balance_after !== undefined) lines.push(["Saldo Simpanan", rupiah(tx.balance_after)]);
  if (tx.installment_no) lines.push(["Angsuran ke", String(tx.installment_no)]);
  if (tx.loan_principal !== undefined) lines.push(["Jumlah Pinjaman", rupiah(tx.loan_principal)]);
  if (tx.remaining_after !== undefined) lines.push(["Sisa Pinjaman", rupiah(tx.remaining_after)]);
  lines.push(["Petugas", tx.by_name]);
  if (tx.note) lines.push(["Catatan", tx.note]);
  return lines;
};

const esc = (s) => String(s ?? "").replace(/[&<>]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;" }[c]));

export const printReceipt = (tx, s, office) => {
  const appName = s.receipt_header || s.app_name;
  const footer = esc(s.receipt_footer || "").replace(/\n/g, "<br/>");
  const rows = receiptLines(tx).map(([k, v]) => `<tr><td>${esc(k)}</td><td class="r">${esc(v)}</td></tr>`).join("");
  const w = window.open("", "_blank", "width=380,height=640");
  if (!w) return false;
  w.document.write(`<html><head><title>Struk ${esc(TX_LABELS[tx.type])}</title><style>
    body{font-family:'Courier New',monospace;width:280px;margin:12px auto;font-size:12px;color:#000}
    h2{text-align:center;margin:0;font-size:15px} p.c{text-align:center;margin:2px 0}
    hr{border:0;border-top:1px dashed #000;margin:8px 0} table{width:100%;border-collapse:collapse}
    td{padding:2px 0;vertical-align:top} td.r{text-align:right;font-weight:bold}</style></head><body>
    <h2>${esc(appName)}</h2><p class="c">${esc(office || "")}</p><hr/><table>${rows}</table><hr/>
    <p class="c">${footer}</p>
    <script>window.onload=function(){window.print();}</script></body></html>`);
  w.document.close();
  return true;
};

export const waUrl = (tx, s) => {
  const text = [`*${s.receipt_header || s.app_name}*`, `Struk ${TX_LABELS[tx.type]}`, "------------------------",
    ...receiptLines(tx).map(([k, v]) => `${k}: ${v}`), "------------------------", s.receipt_footer || ""].join("\n");
  let phone = String(tx.member_phone || "").replace(/\D/g, "");
  if (phone.startsWith("0")) phone = `62${phone.slice(1)}`;
  return `https://wa.me/${phone}?text=${encodeURIComponent(text)}`;
};

export const ReceiptActions = ({ tx, testId = "receipt" }) => {
  const { settings: global } = useSettings();
  const settings = withBranch(global, useBranch());
  const { user } = useAuth();
  return (
    <div className="grid grid-cols-2 gap-2">
      <Button type="button" variant="outline" className="h-11 rounded-full" onClick={() => { if (!printReceipt(tx, settings, user.cabang_name && `Kantor Cabang ${user.cabang_name}`)) toast.error("Izinkan pop-up untuk mencetak struk"); }} data-testid={`${testId}-print`}><Printer className="h-4 w-4 mr-2" />Cetak</Button>
      <Button type="button" asChild className="h-11 rounded-full bg-[#25D366] hover:bg-[#1ebe5a] text-white">
        <a href={waUrl(tx, settings)} target="_blank" rel="noreferrer" data-testid={`${testId}-whatsapp`}><MessageCircle className="h-4 w-4 mr-2" />WhatsApp</a>
      </Button>
    </div>
  );
};

export const ReceiptDialog = ({ tx, onClose }) => (
  <Dialog open={Boolean(tx)} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="sm:max-w-sm" data-testid="receipt-dialog">
      <DialogHeader><DialogTitle className="font-heading">Struk Transaksi</DialogTitle></DialogHeader>
      {tx && (
        <div className="space-y-4">
          <div className="rounded-2xl border border-dashed p-4 font-mono text-xs space-y-1.5" data-testid="receipt-preview">
            {receiptLines(tx).map(([k, v]) => <p key={k} className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="font-semibold text-right">{v}</span></p>)}
          </div>
          <ReceiptActions tx={tx} testId="receipt-dialog" />
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export const useReceipt = () => {
  const [tx, setTx] = useState(null);
  return { tx, open: setTx, close: () => setTx(null) };
};
